import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The frame every breather game sits in.
 *
 * It owns the two things that must be true of all of them and must not be
 * re-implemented three times: the run is SHORT, and it ends in a celebration
 * rather than a failure. There is no losing here - the clock runs out, the
 * child is told what they managed, and they go back to the adventure.
 *
 * A child who would rather stop can stop at any moment. Nothing is lost by
 * leaving early, and nothing is gained by staying.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  );
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return;
    const listen = () => setReduced(query.matches);
    query.addEventListener('change', listen);
    return () => query.removeEventListener('change', listen);
  }, []);
  return reduced;
}

/**
 * A game loop that respects the clock and the tab.
 *
 * Pausing when the tab is hidden matters: a child who wanders off must not
 * come back to a finished game and a timer that ran without them.
 */
export function useGameClock(seconds: number, onDone: () => void) {
  const [left, setLeft] = useState(seconds);
  const done = useRef(false);

  /*
   * The callback lives in a ref, and the interval is created ONCE.
   *
   * This used to depend on `onDone`, which chains back to a plain function
   * redeclared on every render of the games screen. So every render tore the
   * interval down and started a new one, and a one-second tick never reached
   * one second. The clock never fired and the games ran forever - which is
   * exactly how it looked from the sofa.
   */
  const latest = useRef(onDone);
  latest.current = onDone;

  useEffect(() => {
    const tick = setInterval(() => {
      // Paused while the tab is hidden: a child who wanders off must not come
      // back to a finished game and a timer that ran without them.
      if (document.hidden) return;
      setLeft((remaining) => {
        if (remaining <= 1 && !done.current) {
          done.current = true;
          latest.current();
          return 0;
        }
        return Math.max(0, remaining - 1);
      });
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  return left;
}

export function GameShell({
  name,
  scoreLabel,
  score,
  secondsLeft,
  totalSeconds,
  onQuit,
  children,
}: {
  name: string;
  scoreLabel: string;
  score: number;
  secondsLeft: number;
  totalSeconds: number;
  onQuit: () => void;
  children: ReactNode;
}) {
  return (
    <div className="game-shell">
      <div className="game-hud">
        <span className="game-score">
          {scoreLabel} {score}
        </span>
        <span className="game-name">{name}</span>
        <span className="game-timer" aria-label={`${secondsLeft} seconds left`}>
          <span style={{ width: `${(secondsLeft / totalSeconds) * 100}%` }} />
        </span>
      </div>

      <div className="game-stage">{children}</div>

      <button type="button" className="btn btn--quiet" onClick={onQuit}>
        Back to the adventure
      </button>
    </div>
  );
}

/** Shown when the clock runs out. Always warm, never a score to beat. */
export function GameOver({
  name,
  line,
  onDone,
}: {
  name: string;
  line: string;
  onDone: () => void;
}) {
  return (
    <div className="game-over">
      <h1>Nice one!</h1>
      <p className="game-over__line">{line}</p>
      <p className="muted">{name} is done. Ready for the next bit?</p>
      <button type="button" className="btn btn--big" onClick={onDone}>
        Back to the adventure
      </button>
    </div>
  );
}
